import { Injectable, OnDestroy } from '@angular/core';
import { Overlay, OverlayRef, PositionStrategy, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { merge, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { ProgressSpinnerComponent } from './progress-spinner.component';
import { getConfigRequestStatus } from '../store/config/config.selectors';
import { tap, takeUntil, skip } from 'rxjs/operators';
import { getCallStateLoadInsights } from '../store/insights/insights.selectors';
import { CallStateLoadInsights } from '../store/insights/insights.enum';
import { getProjectRequestStatus } from '../store/project/project.selectors';

@Injectable({
  providedIn: 'root',
})
export class ProgressSpinnerService implements OnDestroy {
  private destroyed$ = new Subject();

  private overlayRef: OverlayRef;

  private componentPortal: ComponentPortal<ProgressSpinnerComponent>;

  // keep a count so that concurrent requests result in the spinner being
  // displayed until all of them are completed
  private showCount = 0;

  constructor(private store: Store<any>, private overlay: Overlay) {
    this.createComponentPortal();
    this.createOverlay();
  }

  public show() {
    if (++this.showCount === 1) {
      this.overlayRef.attach(this.componentPortal);
    }
  }

  public hide() {
    if (--this.showCount === 0) {
      this.overlayRef.detach();
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next();
  }

  public setupProgressListeners() {
    merge(
      // skip initial emissions on subscribe
      this.store.select(getConfigRequestStatus).pipe(skip(1)),
      this.store.select(getProjectRequestStatus).pipe(skip(1)),
    )
      .pipe(
        tap((progress) => {
          if (progress) {
            this.show();
          } else {
            this.hide();
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.store
      .select(getCallStateLoadInsights)
      .pipe(
        skip(1), // skip initial emission on subscribe
        tap((callstate) => {
          if (callstate === CallStateLoadInsights.LOADING) {
            this.show();
          } else {
            this.hide();
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  private createOverlay() {
    const config: OverlayConfig = {
      hasBackdrop: true,
      positionStrategy: this.positionGloballyCenter(),
    };
    this.overlayRef = this.overlay.create(config);
  }

  private createComponentPortal() {
    this.componentPortal = new ComponentPortal(ProgressSpinnerComponent);
  }

  private positionGloballyCenter(): PositionStrategy {
    return this.overlay.position().global().centerHorizontally().centerVertically();
  }
}
